import {
  ArrowPathIcon,
  ChatBubbleLeftEllipsisIcon,
  ClockIcon,
  TrashIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { MarkdownText } from '@/components/common/MarkdownText';
import {
  AssignmentPattern,
  CollapsibleDetails,
  DeletionPattern,
  EventRow,
  EventText,
  EventUser,
  StatusChangePattern,
} from '@/components/domain-events-v2/shared';
import {
  DomainEvent,
  RegistryDefinitions,
} from '@/components/domain-events-v2/types';
import { TaskLink } from '@/components/entity-links';

// Helper to get task name
const getTaskName = (event: DomainEvent): string => {
  const payload = event.payload as { task?: { name?: string; type?: string } };
  return payload?.task?.name || payload?.task?.type || 'task';
};

export const taskEvents: RegistryDefinitions = {
  task_created: {
    icon: ClockIcon,
    iconColor: 'text-blue-500',
    priority: 'high',
    render: (event) => {
      // RegistryDefinitions ensures 'event' is inferred correctly as TaskCreatedEvent
      const { payload } = event;
      const taskName = payload.task?.name || payload.task?.type;

      const content = (
        <EventRow>
          <EventUser event={event} />
          <EventText>created</EventText>
          {taskName ? (
            <TaskLink
              entityId={payload.task.id}
              matterId={event.matter}
              variant="badge"
            >
              {taskName}
            </TaskLink>
          ) : (
            <span className="text-gray-500">task</span>
          )}
        </EventRow>
      );

      if (payload.task?.description) {
        return (
          <CollapsibleDetails summary={content}>
            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
              {payload.task.description}
            </p>
          </CollapsibleDetails>
        );
      }

      return content;
    },
    description: (event) => `created task: ${getTaskName(event)}`,
  },

  task_status_changed: {
    icon: ArrowPathIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event) => {
      const { payload } = event;
      return (
        <StatusChangePattern
          event={event}
          entityType="task"
          entityName={
            <TaskLink
              entityId={payload.task.id}
              matterId={event.matter}
              variant="badge"
            >
              {payload.task?.name || 'task'}
            </TaskLink>
          }
          oldStatus={payload.old?.name}
          newStatus={payload.new?.name}
        />
      );
    },
    description: (event) => {
      const p = event.payload;
      return `updated task status to ${p.new?.name}`;
    },
  },

  task_assigned: {
    icon: UserCircleIcon,
    iconColor: 'text-indigo-500',
    priority: 'medium',
    render: (event) => {
      const { payload } = event;
      const assignedTo = payload.new
        ? `${payload.new.first_name || ''} ${payload.new.last_name || ''}`.trim() ||
          payload.new.username
        : null;
      return (
        <AssignmentPattern
          event={event}
          entityName={
            <TaskLink
              entityId={payload.task.id}
              matterId={event.matter}
              variant="badge"
            >
              {payload.task?.name || 'task'}
            </TaskLink>
          }
          assignedTo={assignedTo || undefined}
          unassigned={!payload.new}
        />
      );
    },
    description: () => 'updated task assignment',
  },

  task_deleted: {
    icon: TrashIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event) => {
      const { payload } = event;
      return (
        <DeletionPattern
          event={event}
          entityType="task"
          entityName={payload.task?.name || payload.task?.type || 'task'}
        />
      );
    },
    description: (event) => `removed task: ${getTaskName(event)}`,
  },

  task_update_created: {
    icon: ChatBubbleLeftEllipsisIcon,
    iconColor: 'text-gray-500',
    priority: 'medium',
    render: (event, inline) => {
      const { payload } = event;
      const updateContent = payload.task_update?.update;

      if (inline) {
        return (
          <EventRow>
            <EventUser event={event} />
            <EventText>commented on</EventText>
            <TaskLink
              entityId={payload.task_update?.task_id}
              matterId={event.matter}
              className="font-medium text-gray-900 hover:text-blue-600 transition-colors !no-underline"
            >
              a task
            </TaskLink>
          </EventRow>
        );
      }

      if (updateContent) {
        return (
          <MarkdownText
            content={updateContent}
            className="text-sm text-gray-700 mt-1"
            plainTextClassName="text-sm"
            markdownClassName="prose-sm text-gray-700 prose-p:my-1 prose-p:text-sm prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-li:text-sm prose-pre:text-xs prose-pre:bg-slate-100 prose-pre:px-2 prose-pre:py-1 prose-pre:border prose-pre:border-slate-200 prose-pre:rounded"
          />
        );
      }

      return null;
    },
    description: () => 'commented on a task',
  },
};
